import { useSignal } from '@preact/signals';
import { useEffect, useRef } from 'preact/hooks';

import type { ActivityPause, FixedActivity } from '@amumin/api-client';
import { t } from '@amumin/i18n';
import {
  Button,
  Checkbox,
  DateField,
  Dialog,
  FieldPair,
  Notice,
  RadioList,
  TextField,
} from '@amumin/ui';
import { cx, Icon, Pictogram } from '@amumin/ui-core';

import { api, errorMessage } from '../api.js';
import { addDays, formatRange, pausePresetRange } from '../lib/dates.js';
import { appText, bumpData, messages } from '../state/session.js';
import styles from './FixedActivityEditor.module.css';

// Vaste activiteit met pauzes in de afspraak-editor (0031, golf 2; founder #9: "school is vast,
// maar als ik de vrije dagen toevoeg zoals zomervakantie ... Bij de moskee heb je dat ook, bij
// voetballen heb je ook een zomerstop of winterstop etc."). Eigen blok met eigen staat (module):
// de editor vraagt bij opslaan `commit(seriesId)` en de instellingen gaan via
// PUT /v1/app/activities/fixed/:id. Een nieuwe pauze wordt meteen bewaard (hij kan gedeeld
// worden) en staat daarna aangevinkt.

export interface FixedActivityHandle {
  /** Bewaart de keuzes voor deze herhaling (na het opslaan van de afspraak). */
  commit: (seriesId: string) => Promise<void>;
  /** Is er iets veranderd sinds het openen? */
  dirty: () => boolean;
}

interface State {
  fixed: boolean;
  follow: boolean;
  pauseIds: string[];
}

const PRESETS = ['zomerstop', 'winterstop', 'zomervakantie'] as const;

export function FixedActivityEditor({
  seriesId,
  recurring,
  date,
  handleRef,
}: {
  /** Bestaande afspraak (series_id); null = nieuw. */
  seriesId: string | null;
  /** Herhaalt de afspraak (in het formulier)? */
  recurring: boolean;
  /** Datum van de afspraak (standaard begin van een nieuwe pauze). */
  date: string;
  handleRef: { current: FixedActivityHandle | null };
}) {
  const a = appText.value;
  const x = a.activities;
  const m = messages.value;
  const loaded = useSignal<FixedActivity | null>(null);
  const pauses = useSignal<ActivityPause[]>([]);
  const region = useSignal<'noord' | 'midden' | 'zuid' | null>(null);
  const regionOpen = useSignal(false);
  const regionDraft = useSignal<'noord' | 'midden' | 'zuid' | null>(null);
  const regionBusy = useSignal(false);
  const regionError = useSignal<string | null>(null);
  const openRegion = () => {
    regionDraft.value = region.value;
    regionError.value = null;
    regionOpen.value = true;
  };
  const saveRegion = async () => {
    if (!regionDraft.value || regionBusy.value) return;
    regionBusy.value = true;
    regionError.value = null;
    try {
      await api.family.app.setLayers({ region: regionDraft.value, school_holidays: true });
      region.value = regionDraft.value;
      bumpData();
      regionOpen.value = false;
    } catch (e) {
      regionError.value = errorMessage(e, m);
    } finally {
      regionBusy.value = false;
    }
  };
  const state = useSignal<State>({ fixed: false, follow: false, pauseIds: [] });
  const base = useSignal<State>({ fixed: false, follow: false, pauseIds: [] });
  const form = useSignal<{
    name: string;
    from: string;
    to: string;
    error?: string | undefined;
  } | null>(null);
  const busy = useSignal(false);
  const error = useSignal<string | null>(null);
  // Wat de ouder al koos voordat de huidige stand binnen was, wint (geen terugspringend vinkje).
  const touched = useRef(false);

  useEffect(() => {
    let alive = true;
    loaded.value = null;
    touched.current = false;
    const initial: State = { fixed: false, follow: false, pauseIds: [] };
    state.value = initial;
    base.value = initial;
    void api.activities
      .pauses()
      .then((res) => {
        if (!alive) return;
        pauses.value = res.items;
        region.value = res.region;
      })
      .catch(() => undefined);
    if (seriesId) {
      void api.activities
        .fixed(seriesId)
        .then((f) => {
          if (!alive) return;
          loaded.value = f;
          const s = {
            fixed: f.fixed,
            follow: f.follow_school_holidays,
            pauseIds: [...f.pause_ids],
          };
          base.value = s;
          if (!touched.current) state.value = s;
        })
        .catch(() => undefined);
    }
    return () => {
      alive = false;
    };
  }, [seriesId]);

  const dirty = (): boolean => JSON.stringify(state.value) !== JSON.stringify(base.value);

  handleRef.current = {
    dirty,
    commit: async (id: string) => {
      if (!dirty()) return;
      const s = state.value;
      await api.activities.setFixed(id, {
        fixed: s.fixed && recurring,
        ...(s.fixed && recurring
          ? { follow_school_holidays: s.follow, pause_ids: s.pauseIds }
          : {}),
        if_version: loaded.value?.version ?? 0,
      });
    },
  };

  const set = (changes: Partial<State>) => {
    touched.current = true;
    state.value = { ...state.value, ...changes };
  };
  const togglePause = (id: string) => {
    const ids = state.value.pauseIds;
    set({ pauseIds: ids.includes(id) ? ids.filter((p) => p !== id) : [...ids, id] });
  };

  const startNew = (preset?: (typeof PRESETS)[number]) => {
    // Voorbeelden per seizoen (ronde 1): Winterstop rond Kerst, Zomerstop in de zomer; alleen
    // 'Eigen pauze' begint op de dag van de afspraak.
    const range = preset ? pausePresetRange(preset, date) : { from: date, to: addDays(date, 13) };
    const name = preset ? x.pausePresets[preset] : '';
    form.value = { name, ...range };
    if (preset === 'zomervakantie') void fillSummerHoliday(name, range);
  };

  /** Zomervakantie: de echte schoolvakantie van de regio (dagmarkeringen), als die bekend is. */
  const fillSummerHoliday = async (name: string, range: { from: string; to: string }) => {
    try {
      const y = range.from.slice(0, 4);
      const res = await api.family.app.highlights(`${y}-06-01`, `${y}-09-30`);
      let best: { from: string; to: string; days: number } | null = null;
      for (const d of res.days) {
        for (const h of d.highlights) {
          if (h.kind !== 'school_holiday' || h.day_index !== 1 || h.day_count < 28) continue;
          if (!best || h.day_count > best.days) {
            best = { from: d.date, to: addDays(d.date, h.day_count - 1), days: h.day_count };
          }
        }
      }
      const f = form.value;
      // Alleen als de ouder de datums nog niet zelf aanpaste.
      if (best && f && f.name === name && f.from === range.from && f.to === range.to) {
        form.value = { ...f, from: best.from, to: best.to };
      }
    } catch {
      // stil: dan blijft het seizoensvoorstel staan
    }
  };

  const saveNew = async () => {
    const f = form.value;
    if (!f) return;
    const name = f.name.trim();
    if (!name) {
      form.value = { ...f, error: x.pauseNameRequired };
      return;
    }
    const days = (Date.parse(`${f.to}T00:00:00Z`) - Date.parse(`${f.from}T00:00:00Z`)) / 86_400_000;
    if (!(days >= 0 && days < 366)) {
      form.value = { ...f, error: x.pauseRangeInvalid };
      return;
    }
    busy.value = true;
    error.value = null;
    try {
      const res = await api.activities.createPause({
        name,
        start_date: f.from,
        end_date: f.to,
      });
      pauses.value = [...pauses.value, res.pause].sort((p, q) =>
        p.start_date.localeCompare(q.start_date),
      );
      set({ pauseIds: [...state.value.pauseIds, res.pause.id] });
      form.value = null;
    } catch (e) {
      error.value = errorMessage(e, m);
    } finally {
      busy.value = false;
    }
  };

  const s = state.value;
  const regionName = region.value ? x.regions[region.value] : null;
  const others = (p: ActivityPause): string[] => {
    const root = loaded.value?.root_series_id;
    return p.activities.filter((_, i) => p.series_ids[i] !== root);
  };

  return (
    <section class={cx('field-group', styles.section)} data-name="fixed-activity">
      <div class={styles.head}>
        <span class="field-label">
          <Icon id="calendar-range" size={18} /> {x.section}
        </span>
      </div>
      <Checkbox
        id="fixed-toggle"
        label={x.toggle}
        hint={recurring ? x.toggleHint : x.needsRecurrence}
        checked={s.fixed && recurring}
        disabled={!recurring}
        onChange={(v) => set({ fixed: v })}
      />
      {s.fixed && recurring ? (
        <div class={styles.body}>
          <Checkbox
            id="fixed-school"
            label={x.followSchool}
            hint={regionName ? t(x.followSchoolHint, { region: regionName }) : undefined}
            checked={s.follow}
            onChange={(v) => set({ follow: v })}
          />
          {s.follow && !regionName ? (
            <Notice kind="info" name="fixed-no-region">
              {x.followSchoolNoRegion}{' '}
              <button type="button" class="link-button link-button-small" onClick={openRegion}>
                {a.nav.settings}
              </button>
            </Notice>
          ) : null}

          {s.follow && regionName ? (
            <Button name="fixed-region-change" variant="ghost" onPress={openRegion}>
              {a.nav.settings}
            </Button>
          ) : null}
          <div class={styles.pauses}>
            <p class="field-label">{x.pauses}</p>
            <p class="field-hint">{x.pausesHint}</p>
            {pauses.value.length === 0 ? <p class={styles.empty}>{x.noPauses}</p> : null}
            <ul class={styles.pauseList} data-name="fixed-pauses">
              {pauses.value.map((p) => {
                const on = s.pauseIds.includes(p.id);
                const shared = others(p);
                return (
                  <li key={p.id}>
                    <label
                      class={cx(styles.pause, on && styles.pauseOn)}
                      data-name="fixed-pause"
                      data-pause={p.id}
                    >
                      <input
                        type="checkbox"
                        class={styles.pauseBox}
                        checked={on}
                        onChange={() => togglePause(p.id)}
                      />
                      <Pictogram icon={p.icon} label="" size={28} decorative />
                      <span class={styles.pauseText}>
                        <span class={styles.pauseName}>{p.name}</span>
                        <span class={styles.pauseRange}>
                          {formatRange(p.start_date, p.end_date, m)}
                        </span>
                        {shared.length > 0 ? (
                          <span class={styles.pauseShared}>
                            {t(x.sharedWith, { names: shared.join(', ') })}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>

            {form.value ? (
              <div
                class={styles.form}
                data-name="fixed-pause-form"
                role="group"
                aria-label={x.newPause}
              >
                <TextField
                  id="pause-name"
                  label={x.pauseName}
                  value={form.value.name}
                  placeholder={x.pauseNamePlaceholder}
                  maxLength={60}
                  onInput={(v) =>
                    form.value && (form.value = { ...form.value, name: v, error: undefined })
                  }
                  error={form.value.error === x.pauseNameRequired ? form.value.error : undefined}
                />
                <FieldPair min={150}>
                  <DateField
                    id="pause-from"
                    label={x.pauseFrom}
                    value={form.value.from}
                    onInput={(v) =>
                      form.value &&
                      (form.value = {
                        ...form.value,
                        from: v,
                        to: form.value.to < v ? v : form.value.to,
                        error: undefined,
                      })
                    }
                  />
                  <DateField
                    id="pause-to"
                    label={x.pauseTo}
                    value={form.value.to}
                    min={form.value.from}
                    onInput={(v) =>
                      form.value && (form.value = { ...form.value, to: v, error: undefined })
                    }
                    error={form.value.error === x.pauseRangeInvalid ? form.value.error : undefined}
                  />
                </FieldPair>
                <div class={styles.formActions}>
                  <Button name="pause-cancel" variant="ghost" onPress={() => (form.value = null)}>
                    {x.pauseCancel}
                  </Button>
                  <Button
                    name="pause-save"
                    variant="secondary"
                    icon="check"
                    busy={busy.value}
                    onPress={() => void saveNew()}
                  >
                    {x.pauseAdd}
                  </Button>
                </div>
              </div>
            ) : (
              <div class={styles.presets} role="group" aria-label={x.newPause}>
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    class={styles.preset}
                    data-name="pause-preset"
                    onClick={() => startNew(p)}
                  >
                    <Icon id="plus" size={16} />
                    {x.pausePresets[p]}
                  </button>
                ))}
                <button
                  type="button"
                  class={cx(styles.preset, styles.presetOwn)}
                  data-name="pause-new"
                  onClick={() => startNew()}
                >
                  <Icon id="plus" size={16} />
                  {x.newPause}
                </button>
              </div>
            )}
            {error.value ? (
              <Notice kind="error" name="fixed-error">
                {error.value}
              </Notice>
            ) : null}
          </div>
        </div>
      ) : null}
      <Dialog
        open={regionOpen.value}
        onClose={() => {
          if (!regionBusy.value) regionOpen.value = false;
        }}
        title={m.family.days.layerSchool}
        description={m.family.days.regionHint}
        closeLabel={m.design.dialog.close}
        name="fixed-region-dialog"
        actions={
          <>
            <Button
              name="fixed-region-cancel"
              disabled={regionBusy.value}
              onPress={() => (regionOpen.value = false)}
            >
              {a.common.cancel}
            </Button>
            <Button
              name="fixed-region-save"
              variant="primary"
              busy={regionBusy.value}
              disabled={!regionDraft.value}
              onPress={() => void saveRegion()}
            >
              {a.editor.save}
            </Button>
          </>
        }
      >
        <RadioList
          hideLabel
          name="fixed-region"
          label={m.family.days.layerSchool}
          value={regionDraft.value ?? ''}
          onChange={(v) => (regionDraft.value = v as 'noord' | 'midden' | 'zuid')}
          options={(['noord', 'midden', 'zuid'] as const).map((value) => ({
            value,
            label: x.regions[value],
          }))}
        />
        {regionError.value ? <Notice kind="error">{regionError.value}</Notice> : null}
      </Dialog>
    </section>
  );
}
